/**
 * @fileOverview The tour being played: which of its steps this reader may see, and everything the
 * timeline and the text derive from where the player is. See useViewHelp.ts.
 */

import { computed } from "vue"
import router from "@/router"
import { i18n } from "@/plugins/i18n"
import { useAuthStore } from "@/store/authentication"
import { canReachRoute } from "@/services/routeAccess"
import { isNavEntryEnabled } from "@/services/navigation"
import { hasPermissionsOfAtLeastOne } from "@/services/permissionService"
import { VIEW_HELP_TOURS, findTourForRoute } from "@/services/viewHelp/catalog"
import type { ViewHelpStep, ViewHelpTour } from "@/services/viewHelp/types"
import type { FrameTarget } from "./state"
import { aimFor, type Point } from "@/services/viewHelp/pointer"
import { IMAGE_READING_MS, dwellFor } from "./timing"
import { elapsed, focusRect, landed, skipped, stepIndex, targetIndex, tourKey, worked } from "./state"

// ── gating ────────────────────────────────────────────────────────────────────

/** Where the frame has to be loaded for `step` — its route, and the params and hash it names. */
export const targetOf = (step: ViewHelpStep): FrameTarget => ({
  name: step.route,
  params: step.at?.params,
  query: step.at?.query,
  hash: step.at?.hash,
})

/** Would the router's own guards let this step's route be opened at all? */
const canReachStepRoute = (step: ViewHelpStep): boolean => canReachRoute({ name: step.route, params: step.at?.params })

/**
 * The steps this tenant and user may be shown.
 *
 * `canReachStepRoute` matters even though nothing navigates the host: the frame loads the real app, and
 * a route this user may not open would answer with the paywall or the dashboard *inside the frame*.
 */
export function gatedSteps(candidate: ViewHelpTour | null): ViewHelpStep[] {
  if (!candidate) return []
  const { entitlements } = useAuthStore()

  return candidate.steps.filter(step => {
    const entitled = !step.featureSets || step.featureSets.some(fs => entitlements.includes(fs))
    const permitted = !step.permissions || hasPermissionsOfAtLeastOne(step.permissions).value
    const navigable = !step.navEntry || isNavEntryEnabled(step.navEntry)
    return entitled && permitted && navigable && canReachStepRoute(step)
  })
}

// A step on a route with path params that does not name them cannot be resolved — `canReachStepRoute`
// would throw, and the frame would have no URL to load. Checked here rather than in `tourProblems`,
// which is kept free of the router.
if (import.meta.env.DEV) {
  for (const candidate of VIEW_HELP_TOURS) {
    for (const each of candidate.steps) {
      const path = router.getRoutes().find(route => route.name === each.route)?.path
      if (path === undefined) console.error(`[view-help] step "${each.key}" names unknown route "${each.route}"`)
      else if (path.includes(":") && !each.at?.params) {
        console.error(`[view-help] step "${each.key}" is on "${each.route}" (${path}) but names no at.params`)
      }
    }
  }
}

/** Whether the trigger should be rendered at all on a route. */
export const hasHelpForRoute = (routeName?: string | null): boolean =>
  gatedSteps(findTourForRoute(routeName)).length > 0

// ── the tour and the step ─────────────────────────────────────────────────────

/**
 * The tour that was opened, by its own key — never re-derived from the host's current route. The host
 * page stays live behind the dialog (Ctrl+K, Back, a redirect to the login form), and reading the
 * route here swapped another tour's steps under a frame still showing the first one.
 */
export const tour = computed<ViewHelpTour | null>(() =>
  tourKey.value ? (VIEW_HELP_TOURS.find(candidate => candidate.key === tourKey.value) ?? null) : null
)
export const steps = computed<ViewHelpStep[]>(() => gatedSteps(tour.value))
export const step = computed<ViewHelpStep | null>(() => steps.value[stepIndex.value] ?? null)

/** A step's text, out of the same key the dialog renders. */
export const textOf = (candidate: ViewHelpStep | null | undefined): string => {
  if (!candidate || !tour.value) return ""
  return i18n.global.t(`help.tour.${tour.value.key}.step.${candidate.key}.text`)
}

/** The steps the timeline offers: every gated step except the ones found to have nothing to show. */
export const playable = computed<number[]>(() =>
  steps.value.map((_, index) => index).filter(i => !skipped.value.has(i))
)

/** The first step at or after `start` the timeline still offers, in `direction` — or null. */
export function firstPlayableFrom(start: number, direction: 1 | -1): number | null {
  for (let index = start; index >= 0 && index < steps.value.length; index += direction) {
    if (!skipped.value.has(index)) return index
  }
  return null
}

export const isLast = computed(() => {
  const list = playable.value
  return list.length === 0 || stepIndex.value >= list[list.length - 1]
})

/** How long a step is read for: its text, and every example page it shows. */
const readingOf = (candidate: ViewHelpStep | null | undefined) =>
  dwellFor(textOf(candidate)) + (candidate?.images?.length ?? 0) * IMAGE_READING_MS

export const dwellMs = computed(() => readingOf(step.value))

/** Finished: on the last step, with its reading time used up. Play then means "from the top". */
export const isFinished = computed(() => isLast.value && landed.value && elapsed.value >= dwellMs.value)

// ── the timeline ──────────────────────────────────────────────────────────────

/** The timeline section a step is counted under: its own `section`, or else its view. */
export const sectionOf = (candidate: ViewHelpStep | null | undefined): string | null =>
  candidate ? (candidate.section ?? candidate.route) : null

/**
 * The sections this tour visits, in playing order — how the timeline is grouped. Related ones last,
 * which `tourProblems` holds the steps to.
 */
export const sections = computed<string[]>(() => {
  const list: string[] = []
  for (const index of playable.value) {
    const section = sectionOf(steps.value[index])
    if (section && !list.includes(section)) list.push(section)
  }
  return list
})

/**
 * Whether a section belongs to the tour's related views — drawn apart on the timeline. Asked with a
 * section key or with a route name, since a section that is one view is keyed by its route.
 */
export const isRelatedSection = (name: string) => {
  const related = tour.value?.related ?? []
  return related.includes(name) || steps.value.some(item => sectionOf(item) === name && related.includes(item.route))
}

/** The playable steps of one section, in order. */
export const stepsInSection = (section: string) =>
  playable.value.filter(index => sectionOf(steps.value[index]) === section)

/**
 * The view the walkthrough is on — or, while a move is under way, the one it is **on its way to**.
 *
 * From `displayIndex`, the same index the timeline lights, so the bar answers the moment a move
 * starts: the steps of the view being walked to are laid out while the pointer is still travelling to
 * its tab. Waiting for the step to land left the row showing the view that was being left, for as long
 * as the whole walk took, and then swapping it under the reader at the end.
 */
export const currentSection = computed<string | null>(() => sectionOf(steps.value[displayIndex.value]))

/**
 * The view the next step is on, or null when it stays on this one — so the text can say where the
 * walkthrough is about to go. Nothing about the page changing should come as a surprise.
 */
export const nextRoute = computed<string | null>(() => {
  const following = playable.value.find(index => index > stepIndex.value)
  if (following === undefined) return null
  const route = steps.value[following].route
  return route === step.value?.route ? null : route
})

/**
 * The step the timeline shows as current: where the player is heading while it moves, where it is
 * otherwise. A click on the timeline therefore lights its segment at once.
 */
export const displayIndex = computed(() => targetIndex.value ?? stepIndex.value)

/**
 * How full a step's segment on the timeline is, 0 to 1 — over the **whole** step: the demonstration
 * the pointer gives and then the reading time.
 *
 * Counting only the reading left the segment at nothing for as long as the pointer took to fill in a
 * form, which is the longest thing some steps do, and the step read as stuck. Both parts are measured
 * in the same 1× milliseconds, so the fill is continuous across the moment the step lands: at that
 * point the demonstration's share is already behind it.
 */
export function progressOf(index: number): number {
  const current = displayIndex.value
  if (index < current) return 1
  if (index > current) return 0
  const reading = readingOf(steps.value[index])
  const total = worked.value + reading
  // While a move is under way, `elapsed` still belongs to the step being left: only the demonstration
  // of the step being moved to counts towards its segment.
  const done = worked.value + (targetIndex.value === null ? elapsed.value : 0)
  return total > 0 ? Math.min(1, done / total) : 0
}

/** Where the dot sits, in frame coordinates: the same spot the pointer aims for. */
export const dot = computed<Point | null>(() => (landed.value && focusRect.value ? aimFor(focusRect.value) : null))
